import { Skeleton, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { PinnablePopover } from '@omnea-app/omui/PinnablePopover'
import { PreviewCard } from '@omnea-app/omui/PreviewCard'
import {
  type RequestOperationalStatusDetail,
  useGetRequestOperationalStatus,
} from '../utils/hooks/useGetRequestOperationalStatus'
import { OperationalStatusChip, WithOverdueDays } from './OperationalStatusChip'
import { isSummaryRefreshing } from './summaryRefresh'
import type { OperationalStatus } from './OperationalStatusChip'
import type { ReactNode } from 'react'

type Props = {
  requestId: string
  status: OperationalStatus
  detail: RequestOperationalStatusDetail | null
}

const Section = ({ label, children }: { label: string; children: ReactNode }) => (
  <Stack gap={1}>
    <Typography variant="body-medium" color="text.muted">
      {label}
    </Typography>
    <Typography
      variant="body-regular"
      color="text.default"
      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', textWrap: 'pretty' }}
    >
      {children}
    </Typography>
  </Stack>
)

const getEmptyStateKey = ({
  status,
  isFailedWithNoPrior,
}: {
  status: RequestOperationalStatusDetail['status'] | undefined
  isFailedWithNoPrior: boolean
}): string => {
  if (isFailedWithNoPrior) return 'table-column.status-popover.failed'
  // GENERATED but blank means an override cleared the detail.
  if (status === 'GENERATED') return 'table-column.status-popover.no-detail'
  if (status === 'PENDING') return 'table-column.status-popover.refreshing'
  return 'table-column.status-popover.no-status'
}

const OperationalStatusPopover = ({
  requestId,
  detail,
}: {
  requestId: string
  detail: RequestOperationalStatusDetail | null
}) => {
  const { t } = useTranslation('requests')
  // PinnablePopover unmounts its children when closed, so mounting is already the "is open" signal —
  // gating the query on a separate isOpen prop would leave it disabled for the first painted frame.
  const { data, isLoading, isError } = useGetRequestOperationalStatus({ requestId, initialData: detail })

  if (isLoading) return <PreviewCard content={<Skeleton variant="rectangular" height={80} />} />

  // A failed refresh keeps a summary already on screen; with none to keep, the failure shows.
  if (isError && !data) {
    return (
      <PreviewCard
        content={
          <Typography variant="body-regular" color="text.default">
            {t('table-column.status-popover.load-failed')}
          </Typography>
        }
      />
    )
  }

  const isFailedWithNoPrior = data?.status === 'FAILED' && !data.summary && !data.recommendation
  const isContentlessEmpty = data == null || data.status === 'PENDING' || (!data.summary && !data.recommendation)

  if (isContentlessEmpty) {
    const emptyKey = getEmptyStateKey({ status: data?.status, isFailedWithNoPrior })
    return (
      <PreviewCard
        content={
          <Typography variant="body-regular" color={isFailedWithNoPrior ? 'text.danger.default' : 'text.default'}>
            {t(emptyKey)}
          </Typography>
        }
      />
    )
  }

  return (
    <PreviewCard
      content={
        <Stack gap={3}>
          {data.status === 'FAILED' && (
            <Typography variant="body-medium" color="text.danger.default">
              {t('table-column.status-popover.failed-with-prior')}
            </Typography>
          )}
          {data.summary && <Section label={t('table-column.status-popover.summary')}>{data.summary}</Section>}
          {data.recommendation && (
            <Section label={t('table-column.status-popover.recommendation')}>{data.recommendation.value}</Section>
          )}
        </Stack>
      }
      footer={
        isSummaryRefreshing(data, Date.now()) && (
          <Typography variant="small-regular" color="text.muted">
            {t('table-column.status-popover.refreshing')}
          </Typography>
        )
      }
    />
  )
}

export const OperationalStatusCell = ({ requestId, status, detail }: Props) => {
  const { t } = useTranslation('requests')
  const isTerminal = status.key === 'COMPLETED' || status.key === 'REJECTED'

  const pillChip = <OperationalStatusChip pillKey={status.key} />

  return (
    <WithOverdueDays status={status}>
      {isTerminal ? (
        pillChip
      ) : (
        <PinnablePopover label={t('table-column.status-popover.aria-label')} trigger={pillChip}>
          <OperationalStatusPopover requestId={requestId} detail={detail} />
        </PinnablePopover>
      )}
    </WithOverdueDays>
  )
}
